import * as yup from "yup";
import { validateFileSize } from "../../utils";

export type FixedTermCalculatorFields = {
  loanAmount: number;
  duration: number;
};

export type BuyNowPayLaterCalculatorFields = {
  loanAmount: number;
};

export type RevenueBasedLoanCalculatorFields = {
  loanAmount: number;
  duration: number;
};

export type InvoiceDiscountingCalculatorFields = {
  loanAmount: number;
  duration: number;
  discountRate: number;
};

export type PayableFinancingCalculatorFields = {
  loanAmount: number;
  duration: number;
};

export type UploadInvoiceFields = {
  invoices: File[];
  companyName: string;
};

export type UploadStatementsFields = {
  salesStatements: File[];
};

export type UploadSalesInvoiceFields = {
  salesInvoices: File[];
};

type FixedTermLoanValidationSchemaArgs = {
  loanAmount: {
    min: number;
    max: number;
    unit: string;
  };
  duration: {
    min: number;
    max: number;
  };
};

type BuyNowPayLaterLoanValidationSchemaArgs = {
  loanAmount: {
    min: number;
    max: number;
    unit: string;
  };
};

type RevenueBasedLoanValidationSchemaArgs = {
  loanAmount: {
    min: number;
    max: number;
    unit: string;
  };
  duration: {
    min: number;
    max: number;
  };
};

type InvoiceDiscountingValidationSchemaArgs =
  FixedTermLoanValidationSchemaArgs & {
    discount: {
      min: number;
      max: number;
    };
  };

type PayableFinancingValidationSchemaArgs = {
  loanAmount: {
    min: number;
    max: number;
    unit: string;
  };
  duration: {
    min: number;
    max: number;
  };
};

export const LOAN_FIXED_TERM_INITIAL_VALUES: FixedTermCalculatorFields = {
  loanAmount: 50_000,
  duration: 6,
};

export const LOAN_INVOICE_DISCOUNTING_INITIAL_VALUES: InvoiceDiscountingCalculatorFields =
  {
    loanAmount: 50_000,
    duration: 6,
    discountRate: 50,
  };

export const LOAN_UPLOAD_INVOICE_INITIAL_VALUES: UploadInvoiceFields = {
  invoices: [],
  companyName: "",
};

export const LOAN_UPLOAD_STATEMENTS_INITIAL_VALUES: UploadStatementsFields = {
  salesStatements: [],
};

export const LOAN_UPLOAD_SALES_INVOICES_INITIAL_VALUES: UploadSalesInvoiceFields =
  {
    salesInvoices: [],
  };

export const getFixedTermLoanValidationSchema = ({
  loanAmount,
  duration,
}: FixedTermLoanValidationSchemaArgs) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Amount cannot be less than ${
          loanAmount.unit
        } ${loanAmount.min.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Amount cannot exceed than ${
          loanAmount.unit
        } ${loanAmount.max.toLocaleString()}`
      )
      .required("Loan amount is required"),
    duration: yup.number().min(duration.min).max(duration.max),
  });
};

export const getBuyNowPayLaterLoanValidationSchema = ({
  loanAmount,
}: BuyNowPayLaterLoanValidationSchemaArgs) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Amount cannot be less than ${
          loanAmount.unit
        } ${loanAmount.min.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Amount cannot exceed than ${
          loanAmount.unit
        } ${loanAmount.max.toLocaleString()}`
      ),
  });
};

export const getRevenueBasedLoanValidationSchema = ({
  loanAmount,
  duration,
}: RevenueBasedLoanValidationSchemaArgs) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Amount cannot be less than ${
          loanAmount.unit
        } ${loanAmount.min.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Amount cannot exceed than ${
          loanAmount.unit
        } ${loanAmount.max.toLocaleString()}`
      )
      .required("Loan amount is required"),
    duration: yup.number().min(duration.min).max(duration.max),
  });
};

export const getInvoiceDiscountingValidationSchema = ({
  loanAmount,
  duration,
  discount,
}: InvoiceDiscountingValidationSchemaArgs) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Loan amount must be greater than ${
          loanAmount.unit
        } ${loanAmount.min.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Loan amount cannot be higher than ${
          loanAmount.unit
        } ${loanAmount.max.toLocaleString()}`
      )
      .required("Loan amount is required"),
    duration: yup.number().min(duration.min).max(duration.max),
    discountRate: yup
      .number()
      .min(discount.min, (x) => `Borrow % should be greater than ${x.min}`)
      .max(discount.max, (x) => `Borrow % should be less than ${x.max}`),
  });
};

export const getPayableInvoicingValidationSchema = ({
  loanAmount,
  duration,
}: PayableFinancingValidationSchemaArgs) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Loan amount must be greater than ${
          loanAmount.unit
        } ${loanAmount.min.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Loan amount cannot be higher than ${
          loanAmount.unit
        } ${loanAmount.max.toLocaleString()}`
      )
      .required("Loan amount is required"),
    duration: yup.number().min(duration.min).max(duration.max),
  });
};

export const SALES_STATEMENT_CONFIG = {
  accept: { "application/pdf": [".pdf"] },
  maxFiles: 10,
  minFiles: 1,
  maxSize: 100 * 1024 * 1024,
};

export const SALES_INVOICE_CONFIG = {
  accept: { "application/pdf": [".pdf"] },
  maxFiles: 1,
  minFiles: 1,
  maxSize: 100 * 1024 * 1024,
};

export const LOAN_UPLOAD_SALES_STATEMENT_VALIDATION_SCHEMA = yup
  .object()
  .shape({
    salesStatements: yup
      .array()
      .of(
        yup
          .mixed()
          .test("fileType", "Please provide a valid PDF file", (value: any) => {
            return value?.type === "application/pdf";
          })
          .test("fileSize", "File is larger than 5 MB", validateFileSize(5))
      )
      .min(SALES_STATEMENT_CONFIG.minFiles)
      .max(SALES_STATEMENT_CONFIG.maxFiles)
      .required("Statement is required"),
  });

export const LOAN_UPLOAD_SALES_INVOICE_VALIDATION_SCHEMA = yup.object().shape({
  salesInvoices: yup
    .array()
    .of(
      yup
        .mixed()
        .test("fileType", "Please provide a valid PDF file", (value: any) => {
          return value?.type === "application/pdf";
        })
        .test("fileSize", "File is larger than 5 MB", validateFileSize(5))
    )
    .min(SALES_INVOICE_CONFIG.minFiles)
    .max(SALES_INVOICE_CONFIG.maxFiles)
    .required("Invoice is required"),
});

export const LOAN_UPLOAD_INVOICE_VALIDATION_SCHEMA = yup.object().shape({
  invoices: yup
    .array()
    .min(1)
    .max(1)
    .of(yup.mixed().required())
    .required("Please upload an invoice to continue"),
  companyName: yup
    .string()
    .required(
      "Please enter the name of the company which the invoice attached."
    ),
});
